import React, { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { Banner, Icon, Image, Popover, Stack, Text, useViewport } from "@bookingcom/bui-react";
import { trackWithDefaultStage } from "@flights/et-universal";
import { InfoSignIcon } from "@bookingcom/bui-assets-react/streamline";
import Frame from "../Frame/Frame";
import styles from "./GeniusAirlinesBanner.module.css";
import { getIndexCampaigns } from "store/indexCampaigns/selectors";
import useUserAgent from "hooks/useUserAgent";

const GeniusAirlinesBanner = ({ showText }: { showText?: boolean }) => {
  const geniusAirlineBannerData = useSelector(getIndexCampaigns).geniusAirlines;
  const { isMobile } = useUserAgent();
  const { isSmall, isLarge } = useViewport();

  const showBanner = !!geniusAirlineBannerData && trackWithDefaultStage("flights_web_cat_summer_deal", 1);

  const carriersList = useMemo(() => {
    if (!showBanner) {
      return null;
    }

    const airlines = isMobile ? geniusAirlineBannerData.airlines.slice(0, 3) : geniusAirlineBannerData.airlines;

    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        wrap={trackWithDefaultStage("flights_web_cat_summer_deal", 1) === 2 && !isSmall ? "nowrap" : "wrap"}
        gap={showText && isLarge ? 8 : !isSmall ? 2 : 1}
        attributes={{ role: "listbox" }}
        className={styles.airlinesList}
      >
        {airlines.map(
          ({ name, imageUrl }) =>
            !!name &&
            !!imageUrl && (
              <Stack
                key={name}
                justifyContent="space-between"
                alignItems="center"
                wrap="wrap"
                attributes={{ role: "listitem" }}
              >
                <Image width="32px" src={imageUrl} alt="" />
                <Text variant="small_2" align="center">
                  {name}
                </Text>
              </Stack>
            )
        )}
      </Stack>
    );
  }, [geniusAirlineBannerData?.airlines, isLarge, isSmall, showBanner, showText, isMobile]);

  if (!showBanner || !carriersList) {
    return null;
  }

  if (showText) {
    return (
      <Banner dismissible={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Stack direction="row" alignItems="start" gap={4} className={styles.imageWrapper}>
            <Stack.Item>
              <Image
                height="64px"
                width="64px"
                contentMode="fit"
                asset={{
                  setName: "illustrations-traveller",
                  assetName: "GeniusFlightsBadge"
                }}
              />
            </Stack.Item>
            <Stack.Item className={styles.titleItem}>
              {_Title({
                title: geniusAirlineBannerData.title,
                tooltip: geniusAirlineBannerData.tooltip
              })}
              <Text variant="body_2" className={styles.subtitle}>
                {geniusAirlineBannerData.subtitle}
              </Text>
            </Stack.Item>
          </Stack>
          <Stack.Item className={styles.carriersWrapper}>
            <Frame mt={4} mb={4}>
              {carriersList}
            </Frame>
          </Stack.Item>
        </Stack>
      </Banner>
    );
  }

  return (
    <Banner
      title={_Title({
        title: geniusAirlineBannerData.title,
        tooltip: geniusAirlineBannerData.tooltip
      })}
      dismissible={false}
    >
      <Frame mt={4} mb={4}>
        {carriersList}
      </Frame>
    </Banner>
  );
};

const _Title = ({ title, tooltip }: { title: string; tooltip: string }) => {
  return (
    <Text variant="strong_1" attributes={{ "aria-describedby": "genius_airlines_tooltip", style: { flex: 1 } }}>
      {title}{" "}
      <Popover
        position="top"
        triggerType="hover"
        navigationMode="tab"
        trapFocusMode="soft"
        hideClose={true}
        keepMounted={true}
      >
        <Popover.Trigger>
          {(attrs) => (
            <span
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              {...attrs}
              aria-labelledby="genius_airlines_tooltip"
              className={styles.tooltip__icon__wrapper}
            >
              <Icon attributes={{ "aria-hidden": true }} svg={<InfoSignIcon />} size="smallest" />
            </span>
          )}
        </Popover.Trigger>
        <Popover.Content
          attributes={{
            "aria-label": tooltip,
            id: "genius_airlines_tooltip",
            role: "tooltip"
          }}
        >
          <Text variant="body_1">{tooltip}</Text>
        </Popover.Content>
      </Popover>
    </Text>
  );
};

export default memo(GeniusAirlinesBanner);
